/**
 * Poster layout field metadata — section codes, labels, and descriptions.
 * Used by InfoTooltip and LayoutDiagram to provide contextual help.
 */

export const SECTION_GROUPS = [
  { code: "A", name: "Page Canvas",    color: "#6366f1" },
  { code: "B", name: "Page Margins",   color: "#f59e0b" },
  { code: "C", name: "Title Area",     color: "#10b981" },
  { code: "D", name: "Data Columns",   color: "#3b82f6" },
  { code: "E", name: "Date Header",    color: "#8b5cf6" },
  { code: "F", name: "Fixture Entry",  color: "#ef4444" },
  { code: "G", name: "Spacing",        color: "#14b8a6" },
  { code: "H", name: "Logo Area",      color: "#f97316" },
  { code: "I", name: "Split & Zoom",   color: "#ec4899" },
];

const SECTION_COLOR_MAP = Object.fromEntries(
  SECTION_GROUPS.map((s) => [s.code, s.color])
);

export const POSTER_FIELD_DESCRIPTIONS = {
  // A — Page Canvas
  page_width: {
    code: "A1", section: "A", label: "Page Width",
    description: "Total canvas width in pixels. Landscape default: 3008, Portrait/Instagram: 1700.",
  },
  page_height: {
    code: "A2", section: "A", label: "Page Height",
    description: "Total canvas height in pixels. Landscape default: 1700, Portrait/Instagram: 2404.",
  },

  // B — Page Margins
  page_margin_side: {
    code: "B1", section: "B", label: "Side Margin",
    description: "Left and right margin of the content area. Pushes data columns inward from the page edges.",
  },
  page_margin_side_extra: {
    code: "B2", section: "B", label: "Side Margin Extra",
    description: "Additional side padding applied inside the main side margin for fine-tuning column placement.",
  },
  page_margin_top: {
    code: "B3", section: "B", label: "Top Margin",
    description: "Top margin of the page canvas, above the title area.",
  },
  page_margin_bottom: {
    code: "B4", section: "B", label: "Bottom Margin",
    description: "Bottom margin of the page canvas, below the logo area.",
  },

  // C — Title Area
  title_margin_top: {
    code: "C1", section: "C", label: "Title Margin Top",
    description: "Space above the title text, measured from the top margin edge.",
  },
  font_size_title: {
    code: "C2", section: "C", label: "Title Font Size",
    description: "Font size of the poster title text (e.g. the channel name or event title).",
  },
  title_margin_bottom: {
    code: "C3", section: "C", label: "Title Margin Bottom",
    description: "Space below the title text, before the data columns area begins.",
  },

  // D — Data Columns
  portion_width: {
    code: "D1", section: "D", label: "Column Width",
    description: "Width of each data column (portion) when using multi-column layout.",
  },
  portion_width_single_column: {
    code: "D2", section: "D", label: "Single Column Width",
    description: "Width of the data column when the poster uses a single-column layout.",
  },
  nr_of_columns: {
    code: "D3", section: "D", label: "Number of Columns",
    description: "How many data columns to display side-by-side in the fixture area.",
  },
  single_column_extra_margin: {
    code: "D4", section: "D", label: "Single Col Extra Margin",
    description: "Extra side margin added when using a single column layout for visual centering.",
  },

  // E — Date Header
  font_size_date_day: {
    code: "E1", section: "E", label: "Date Day Font Size",
    description: "Font size for the day number in the date header (e.g. '15' in 'Saturday 15').",
  },
  font_size_date_month: {
    code: "E2", section: "E", label: "Date Month Font Size",
    description: "Font size for the month/year text in the date header (e.g. 'March 2025').",
  },
  font_size_matchday: {
    code: "E3", section: "E", label: "Matchday Font Size",
    description: "Font size for the matchday label (e.g. 'Matchday 28').",
  },
  matchday_margin_bottom: {
    code: "E4", section: "E", label: "Matchday Margin Bottom",
    description: "Space below the matchday label before fixture entries begin.",
  },

  // F — Fixture Entry
  font_size_primary: {
    code: "F1", section: "F", label: "Primary Font Size",
    description: "Main font size for fixture text (team names, scores).",
  },
  font_size_secondary: {
    code: "F2", section: "F", label: "Secondary Font Size",
    description: "Smaller font size for secondary fixture info (competition name, venue).",
  },
  time_margin_right: {
    code: "F3", section: "F", label: "Time Margin Right",
    description: "Right margin after the match time, separating it from team names.",
  },
  entry_margin_bottom: {
    code: "F4", section: "F", label: "Entry Margin Bottom",
    description: "Vertical space between individual fixture entries.",
  },
  entry_max_lines: {
    code: "F5", section: "F", label: "Max Entry Lines",
    description: "Maximum number of text lines per fixture entry before truncation.",
  },

  // G — Spacing
  portion_vertical_spacing: {
    code: "G1", section: "G", label: "Vertical Spacing",
    description: "Vertical gap between date groups within a data column.",
  },
  portion_line_height: {
    code: "G2", section: "G", label: "Line Height",
    description: "Line height (leading) for text within data columns.",
  },

  // H — Logo Area
  logo_position_y: {
    code: "H1", section: "H", label: "Logo Y Position",
    description: "Vertical position (from top) where the bar logo is placed on the poster.",
  },
  logo_margin_top: {
    code: "H2", section: "H", label: "Logo Margin Top",
    description: "Minimum space above the logo, ensuring it doesn't overlap with data columns.",
  },

  // I — Split & Zoom
  min_entries_for_split: {
    code: "I1", section: "I", label: "Min Entries for Split",
    description: "Minimum total fixture entries before the poster splits content across multiple columns.",
  },
  min_entries_per_column_split: {
    code: "I2", section: "I", label: "Min per Column Split",
    description: "Minimum entries needed per column when deciding whether to split into columns.",
  },
  max_zoom_multiplier: {
    code: "I3", section: "I", label: "Max Zoom Multiplier",
    description: "Maximum scale factor applied when auto-zooming content to fill available space (e.g. 1.6 = 160%).",
  },
};

/** Helper: get section color for a field key */
export function getSectionColor(fieldKey) {
  const field = POSTER_FIELD_DESCRIPTIONS[fieldKey];
  if (!field) return "#6b7280";
  return SECTION_COLOR_MAP[field.section] || "#6b7280";
}
